import { useCallback, useEffect, useRef, useState } from 'react';
import { useOnVisible } from '@hooks/use-on-visible';

interface UseTweenConfig {
  startingValue?: number;
  targetValue?: number;
  duration?: number;
  easingFn?: (x: number) => number;
  infinite?: boolean;
  yoyo?: boolean;
  onTweenComplete?: () => void;
}

export const useTween = ({
  startingValue = 0,
  targetValue = 0,
  duration = 0, // in seconds
  easingFn = (x) => x,
  infinite = false,
  yoyo = false,
  onTweenComplete,
}: UseTweenConfig): [
  number,
  (newTargetValue: number) => void,
  (endCallback: () => void) => void,
] => {
  const [fromVal, setFromVal] = useState(startingValue);
  const [toVal, setToVal] = useState(targetValue);
  const [currentVal, setCurrentVal] = useState(startingValue);
  const [tweenResetTime, setTweenResetTime] = useState(Date.now());
  const [isPlayingForward, setIsPlayingForward] = useState(true);

  const resettingEndTimeOnVisible = useRef(false);
  const onTweenCompleteRef = useRef(onTweenComplete);
  const rafID = useRef(null);

  const createUpdater = useCallback((from, to, endTime, playingForward) => {
    if (rafID.current) {
      window.cancelAnimationFrame(rafID.current);
      rafID.current = null;
    }

    return function update() {
      const remainingTime = endTime - Date.now();
      const timeProgress = 1 - remainingTime / (duration * 1000);
      const hasCompleted = remainingTime <= 0;

      if (hasCompleted) {
        if (rafID.current) {
          window.cancelAnimationFrame(rafID.current);
          rafID.current = null;
        }

        if (!infinite || !!onTweenCompleteRef.current) {
          if (currentVal != to) {
            setCurrentVal(to);
          }

          onTweenCompleteRef.current?.();
          return;
        } else if (infinite && !yoyo) {
          setCurrentVal(from);
          setTweenResetTime(Date.now());
          return;
        } else if (infinite && yoyo) {
          setToVal(playingForward ? startingValue : targetValue);
          setFromVal(playingForward ? targetValue : startingValue);
          setIsPlayingForward(!playingForward);
          setTweenResetTime(Date.now());
          return;
        }
      } else {
        const tweenProgress = (to - from) * timeProgress;
        setCurrentVal(from + tweenProgress);
      }

      rafID.current = window.requestAnimationFrame(update);
    };
  }, []);

  const updater = useCallback(() => {
    if (resettingEndTimeOnVisible.current) {
      resettingEndTimeOnVisible.current = false;
      const proportionCompleted = (currentVal - fromVal) / (toVal - fromVal);
      const endTime = Date.now() + (1 - proportionCompleted) * duration * 1000;
      return createUpdater(currentVal, toVal, endTime, isPlayingForward)();
    } else {
      const endTime = Date.now() + duration * 1000;
      return createUpdater(currentVal, toVal, endTime, isPlayingForward)();
    }
  }, [toVal, tweenResetTime, isPlayingForward]);

  const onVisibleCallback = useCallback(() => {
    if (infinite && currentVal != toVal) {
      resettingEndTimeOnVisible.current = true;
      setTweenResetTime(Date.now());
    }
  }, [currentVal, toVal]);

  // reset the tweenEndTime based on current time and position
  useOnVisible(onVisibleCallback);

  const resetTween = (newTargetValue: number) => {
    setTweenResetTime(Date.now());
    setToVal(newTargetValue);
  };

  const setEndCallback = (cb: () => void) => {
    onTweenCompleteRef.current = cb;
  };

  useEffect(() => {
    updater();
  }, [updater]);

  // kick things off
  useEffect(() => {
    if (currentVal != toVal) {
      updater();
    }

    // cleanup raF when unmounting
    return () => {
      if (rafID.current) {
        window.cancelAnimationFrame(rafID.current);
      }
    };
  }, []);

  return [easingFn(currentVal), resetTween, setEndCallback];
};
